import { forwardRef, useId, type ReactNode } from 'react';

import styles from './SegmentedControl.module.css';

/**
 * SegmentedControl — exactly one value, always.
 *
 * Handoff, Forms 4 and the migration map: "Reader / Sensor as two checkboxes"
 * becomes "Segmented control — one value, always valid". Two checkboxes admit
 * four states, two of which are nonsense. This component is the shape of the
 * data, so the nonsense has to be unrepresentable rather than validated:
 *
 * - `value` is a required prop with no default and no `null` in its type, so
 *   "neither" cannot be passed.
 * - The options are native same-`name` radios, so "both" cannot be reached by
 *   any interaction — the browser enforces it, not a handler.
 * - A `value` that matches no option would render as neither-checked, which is
 *   the one hole the type system leaves open. A DEV guard closes it.
 *
 * Native radios also bring the whole keyboard contract for free: Tab enters the
 * group at the checked option, arrow keys move *and* select, and the group is
 * one tab stop. A div-with-roving-tabindex reimplementation of that is a lot of
 * code to arrive back where the platform already was.
 *
 * A `<fieldset>`/`<legend>` rather than a labelled `<div role="radiogroup">`:
 * a `<label>` cannot label a group, and the legend is the only element that
 * names one natively in every screen reader.
 */

export interface SegmentedOption {
  value: string;
  label: string;
  /** Decorative glyph before the label. Hidden from assistive technology. */
  icon?: ReactNode;
}

export interface SegmentedControlProps {
  /** Names the group. Rendered as the legend above the control. */
  label: string;
  options: readonly SegmentedOption[];
  /** Required. There is no "nothing selected" and no default. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * Radio group name. Defaults to a generated id — two SegmentedControls on one
   * page must not share a name or they become one group.
   */
  name?: string;
  /**
   * `inline` puts the label BESIDE the control instead of above it — for a
   * card head, where a stacked legend would make the head a field row tall.
   * The legend stays (visually hidden) so the group keeps its name.
   */
  labelPlacement?: 'above' | 'inline';
  className?: string;
}

export const SegmentedControl = forwardRef<HTMLFieldSetElement, SegmentedControlProps>(
  function SegmentedControl(
    { label, options, value, onChange, disabled = false, name, labelPlacement = 'above', className },
    ref,
  ) {
    const generatedName = useId();
    const groupName = name ?? generatedName;

    if (import.meta.env.DEV && !options.some((option) => option.value === value)) {
      console.error(
        `SegmentedControl ("${label}") has value "${value}", which is not one of its ` +
          `options (${options.map((option) => option.value).join(', ')}). ` +
          'That renders the both-or-neither state this control exists to remove.',
      );
    }

    return (
      <fieldset
        ref={ref}
        className={[styles['group'], labelPlacement === 'inline' ? styles['inline'] : null, className]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
      >
        <legend className={styles['legend']}>{label}</legend>
        {labelPlacement === 'inline' ? (
          <span className={styles['inlineLabel']} aria-hidden="true">
            {label}
          </span>
        ) : null}

        <div
          className={[styles['seg'], disabled ? styles['segDisabled'] : null]
            .filter(Boolean)
            .join(' ')}
        >
          {options.map((option) => (
            <label key={option.value} className={styles['option']}>
              <input
                className={styles['radio']}
                type="radio"
                name={groupName}
                value={option.value}
                checked={option.value === value}
                onChange={() => onChange(option.value)}
              />
              <span className={styles['face']}>
                {option.icon ? <span aria-hidden="true">{option.icon}</span> : null}
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  },
);
